import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { IconX } from '@tabler/icons-react';

/**
 * Universal Modal Primitive
 * Replaces: All standalone CSS modals (e.g., ShareModal.css, DeleteConfirmModal.css)
 * Uses React Portals to ensure the overlay covers the entire screen.
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md',
  hideCloseButton = false,
  hideHeader = false,
  containerClassName = '',
  allowOverflow = false,
  floatingContent = null
}) => {
  const modalRef = useRef(null);

  // Focus first interactive element when modal opens for accessibility/screen readers
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const autoFocusEl = modalRef.current.querySelector('[autoFocus]');
      if (autoFocusEl) {
        autoFocusEl.focus();
      } else {
        const focusable = modalRef.current.querySelectorAll(FOCUSABLE);
        focusable?.[0]?.focus();
      }
    }
  }, [isOpen]);

  // Focus trap: Esc closes, Tab/Shift+Tab cycle within modal, body overflow toggle
  useEffect(() => {
    const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) { onClose(); return; }

      if (e.key === 'Tab' && isOpen && modalRef.current) {
        const focusable = Array.from(modalRef.current.querySelectorAll(FOCUSABLE));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { last?.focus(); e.preventDefault(); }
        } else {
          if (document.activeElement === last) { first?.focus(); e.preventDefault(); }
        }
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full m-4'
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`relative w-full ${sizes[size]} ${containerClassName}`}>
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`relative w-full bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] outline-none ${allowOverflow ? '' : 'overflow-hidden'}`}
        >
          {/* Header */}
          {!hideHeader && (
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              {title && (
                <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                  {title}
                </h2>
              )}
              {!hideCloseButton && (
                <button
                  onClick={onClose}
                  className="p-1.5 ml-auto text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label="Close modal"
                >
                  <IconX size={20} />
                </button>
              )}
            </div>
          )}

        {/* scrollable Body */}
        <div className="p-4 overflow-y-auto overflow-x-hidden flex-1">
          <p id="modal-description" className="sr-only">{title || 'Modal content'}</p>
          {children}
        </div>
        </div>
        {floatingContent && (
          <div className="hidden xl:flex absolute top-1/2 -translate-y-1/2 z-50" style={{ right: '-60px' }}>
            {floatingContent}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
  hideCloseButton: PropTypes.bool,
  hideHeader: PropTypes.bool,
  containerClassName: PropTypes.string,
  allowOverflow: PropTypes.bool,
  floatingContent: PropTypes.node
};

export default Modal;
